import { Controller } from "@hotwired/stimulus"

// The in-page FAQ editor (TMS-1747), drawn into a landing page's FAQ frame. Each question,
// category and settings form saves on its own, and every save redraws the editor, so a form
// still holding unsaved edits would be lost by it — or by Done, or by leaving the page. Each
// of those asks first. Cancel closes a form; one with edits is discarded by redrawing the
// editor, since the rich-text answer cannot be reset in place.
const MESSAGE = "You have unsaved FAQ changes. Discard them?"

export default class extends Controller {
  static values = { url: String }

  connect() {
    this.snapshots = new Map()
    this.boundClick = this.guardClick.bind(this)
    this.boundSubmit = this.guardSubmit.bind(this)
    this.boundBeforeUnload = this.beforeUnload.bind(this)
    this.boundBeforeVisit = this.beforeVisit.bind(this)

    this.element.addEventListener("click", this.boundClick)
    this.element.addEventListener("submit", this.boundSubmit)
    window.addEventListener("beforeunload", this.boundBeforeUnload)
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit)

    // Wait a frame so the rich-text editors finish writing their hidden inputs; that is
    // not an edit.
    requestAnimationFrame(() => this.snapshot())
    this.element.querySelector("[data-faq-editor-panel]:not([hidden]) input:not([type=hidden])")?.focus()
  }

  disconnect() {
    this.element.removeEventListener("click", this.boundClick)
    this.element.removeEventListener("submit", this.boundSubmit)
    window.removeEventListener("beforeunload", this.boundBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit)
  }

  open({ params: { panel } }) {
    const target = document.getElementById(panel)
    if (!target) return

    target.hidden = false
    target.querySelector("input:not([type=hidden]), [contenteditable=true]")?.focus()
  }

  cancel(event) {
    const panel = event.target.closest("[data-faq-editor-panel]")
    if (!panel) return
    if (!this.dirtyForms().some((form) => panel.contains(form))) {
      panel.hidden = true
      return
    }
    if (this.dirtyForms().some((form) => !panel.contains(form)) && !window.confirm(MESSAGE)) return

    this.redraw()
  }

  // A form's own submit saves it; any other form's edits would be lost by the redraw.
  guardSubmit(event) {
    if (this.dirtyForms().some((form) => form !== event.target) && !window.confirm(MESSAGE)) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  guardClick(event) {
    const link = event.target.closest("a[href]")
    if (!link || link.target === "_blank" || event.defaultPrevented || event.metaKey || event.ctrlKey) return
    if (this.dirtyForms().length === 0) return

    if (window.confirm(MESSAGE)) {
      this.leaving = true
    } else {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  beforeUnload(event) {
    if (this.leaving || this.dirtyForms().length === 0) return
    event.preventDefault()
    event.returnValue = ""
  }

  beforeVisit(event) {
    if (this.leaving || this.dirtyForms().length === 0) return
    if (!window.confirm(MESSAGE)) event.preventDefault()
  }

  redraw() {
    this.leaving = true
    const frame = this.element.closest("turbo-frame")
    if (frame.getAttribute("src") === this.urlValue) {
      frame.reload()
    } else {
      frame.src = this.urlValue
    }
  }

  snapshot() {
    this.element.querySelectorAll("form").forEach((form) => this.snapshots.set(form, this.serialize(form)))
  }

  dirtyForms() {
    return [...this.element.querySelectorAll("form")].filter((form) => {
      const initial = this.snapshots.get(form)
      return initial != null && this.serialize(form) !== initial
    })
  }

  serialize(form) {
    return new URLSearchParams(new FormData(form)).toString()
  }
}
